import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../../../context/configurationContext';

import Field, { hasDataInFields } from '../../Field';
import BlockBuilder from '../../BlockBuilder';
import { Link } from '../../Primitives/Link';

import { ResponsiveImage } from '../../../../components';

import SectionContainer from '../SectionContainer';
import css from './SectionColumns.module.css';

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 265px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};
const getResponsiveImageSizes = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.responsiveImageSizes : COLUMN_CONFIG[0].responsiveImageSizes;
};

const isImageAsset = asset => asset?.type === 'imageAsset';

const PROVIDER_OPTIONS_SECTION_ID = 'para-proveedores';
const PROVIDER_OPTION_DESTINATIONS = ['/s/request-quote', '/s/pedir-cotizacion'];

/**
 * Renders the provider option as one accessible link while keeping its Console-managed media and copy.
 *
 * @param {Object} props
 * @param {Object} props.block Console block configuration
 * @param {string} props.href in-app destination for the provider option
 * @param {string} props.responsiveImageSizes sizes attribute for the block image
 * @param {Object} props.options Page Builder field options
 * @returns {JSX.Element} linked provider option card
 */
const ProviderOptionCard = props => {
  const { block, href, responsiveImageSizes, options } = props;
  const { media, text, title } = block;
  const titleText = title?.content;
  const mediaWithoutLink = media ? { ...media, link: { fieldType: 'none' } } : media;

  return (
    <Link className={css.providerOptionLink} href={href} title={titleText}>
      <article className={css.providerOptionCard}>
        <Field
          data={mediaWithoutLink}
          className={css.customerOptionMedia}
          sizes={responsiveImageSizes}
          options={options}
          suppressLinkTitle
        />
        <div className={css.customerOptionText}>
          <Field data={title} options={options} />
          <Field data={text} options={options} />
        </div>
      </article>
    </Link>
  );
};

const getServicesDirectoryLinks = blocks =>
  blocks.flatMap(block => {
    const content = block?.text?.fieldType === 'markdown' ? block.text.content : '';
    return [...content.matchAll(/\[([^\]]+)\]\(([^)]+)\)/g)].map(([, label, href]) => ({
      label,
      href,
    }));
  });

const getFiveColumnPosition = (index, total) => {
  const numberOfColumns = 5;
  const completeRows = Math.floor(total / numberOfColumns);
  const remainder = total % numberOfColumns;
  const firstIndexOnLastRow = completeRows * numberOfColumns;
  const isOnIncompleteLastRow = remainder > 0 && index >= firstIndexOnLastRow;

  return isOnIncompleteLastRow
    ? {
        column: numberOfColumns - remainder + 1 + index - firstIndexOnLastRow,
        row: completeRows + 1,
      }
    : { column: (index % numberOfColumns) + 1, row: Math.floor(index / numberOfColumns) + 1 };
};

const getBalancedFiveColumnPositions = (total, longLinkIndexes) => {
  const numberOfColumns = 5;
  const baseColumnSize = Math.floor(total / numberOfColumns);
  const largerColumnCount = total % numberOfColumns;
  const columnSizes = Array.from(
    { length: numberOfColumns },
    (_, index) => baseColumnSize + (index >= numberOfColumns - largerColumnCount ? 1 : 0)
  );
  const baseLongCount = Math.floor(longLinkIndexes.size / numberOfColumns);
  const extraLongCount = longLinkIndexes.size % numberOfColumns;
  const longTargets = Array.from(
    { length: numberOfColumns },
    (_, index) => baseLongCount + (index < extraLongCount ? 1 : 0)
  );
  const shortTargets = columnSizes.map((size, index) => size - longTargets[index]);
  const columns = Array.from({ length: numberOfColumns }, () => []);
  const longCounts = Array(numberOfColumns).fill(0);
  const shortCounts = Array(numberOfColumns).fill(0);

  Array.from({ length: total }, (_, index) => index).forEach(index => {
    const isLong = longLinkIndexes.has(index);
    const typeCounts = isLong ? longCounts : shortCounts;
    const typeTargets = isLong ? longTargets : shortTargets;
    const availableColumns = columns
      .map((column, columnIndex) => ({ column, columnIndex }))
      .filter(
        ({ column, columnIndex }) =>
          column.length < columnSizes[columnIndex] &&
          typeCounts[columnIndex] < typeTargets[columnIndex]
      )
      .sort(
        (a, b) =>
          a.column.length - b.column.length || a.columnIndex - b.columnIndex
      );
    const columnIndex = availableColumns[0].columnIndex;

    columns[columnIndex].push(index);
    typeCounts[columnIndex] += 1;
  });

  return columns.reduce((positions, column, columnIndex) => {
    column.forEach((linkIndex, rowIndex) => {
      positions[linkIndex] = { column: columnIndex + 1, row: rowIndex + 1 };
    });
    return positions;
  }, []);
};

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {'columns'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionColumns = props => {
  const config = useConfiguration();
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    sectionName,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;
  const isCustomerOptionsSection = sectionName === 'Cuatro opciones para clientes';
  const isProviderOptionsSection = sectionId === PROVIDER_OPTIONS_SECTION_ID;
  const isServicesDirectorySection = sectionId === 'categorias-servicios';
  const brandSymbol = config.branding.appIcon;
  const brandSymbolVariants = Object.keys(brandSymbol?.attributes?.variants || {});
  const servicesDirectoryLinks = getServicesDirectoryLinks(blocks);
  const servicesDirectoryLinkRefs = React.useRef([]);
  const [balancedLinkPositions, setBalancedLinkPositions] = React.useState(null);
  const servicesDirectoryLinkKey = servicesDirectoryLinks.map(({ label }) => label).join('|');

  React.useEffect(() => {
    if (!isServicesDirectorySection) {
      return undefined;
    }

    let resizeFrame;
    const updateBalancedPositions = () => {
      if (!window.matchMedia('(min-width: 1280px)').matches) {
        setBalancedLinkPositions(null);
        return;
      }

      const longLinkIndexes = new Set(
        servicesDirectoryLinkRefs.current
          .map((item, index) => {
            const link = item?.querySelector('a');
            const lineHeight = link ? parseFloat(window.getComputedStyle(link).lineHeight) : 0;
            const isLong = link && link.getBoundingClientRect().height > lineHeight * 1.5;
            return isLong ? index : null;
          })
          .filter(index => index !== null)
      );

      setBalancedLinkPositions(
        getBalancedFiveColumnPositions(servicesDirectoryLinks.length, longLinkIndexes)
      );
    };
    const schedulePositionUpdate = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(updateBalancedPositions);
    };

    schedulePositionUpdate();
    window.addEventListener('resize', schedulePositionUpdate);
    return () => {
      window.cancelAnimationFrame(resizeFrame);
      window.removeEventListener('resize', schedulePositionUpdate);
    };
  }, [isServicesDirectorySection, servicesDirectoryLinkKey, servicesDirectoryLinks.length]);

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(className, {
        [css.customerOptionsSection]: isCustomerOptionsSection,
        [css.providerOptionsSection]: isProviderOptionsSection,
        [css.servicesDirectorySection]: isServicesDirectorySection,
      })}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields && !isServicesDirectorySection ? (
        <header
          className={classNames(defaultClasses.sectionDetails, {
            [css.customerOptionsHeader]: isCustomerOptionsSection || isProviderOptionsSection,
          })}
        >
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      {hasBlocks ? (
        isServicesDirectorySection ? (
          <div className={classNames(defaultClasses.blockContainer, css.servicesDirectoryLayout)}>
            <div className={css.servicesDirectoryRail}>
              <span className={css.servicesDirectoryRailLine} />
              {isImageAsset(brandSymbol) ? (
                <div className={css.servicesDirectorySymbol}>
                  <ResponsiveImage
                    rootClassName={css.servicesDirectorySymbolImage}
                    alt=""
                    image={brandSymbol}
                    variants={brandSymbolVariants}
                    sizes="56px"
                  />
                </div>
              ) : null}
              <span className={css.servicesDirectoryRailLine} />
            </div>
            <header className={css.servicesDirectoryHeading}>
              <Field data={title} options={fieldOptions} />
            </header>
            <div className={css.servicesDirectoryLinks}>
              {servicesDirectoryLinks.map(({ label, href }, index) => {
                const { column, row } =
                  balancedLinkPositions?.[index] ||
                  getFiveColumnPosition(index, servicesDirectoryLinks.length);
                return (
                  <span
                    key={`${href}-${index}`}
                    ref={item => {
                      servicesDirectoryLinkRefs.current[index] = item;
                    }}
                    className={css.servicesDirectoryLinkItem}
                    style={{
                      '--services-directory-column': column,
                      '--services-directory-row': row,
                    }}
                  >
                    <Link className={css.servicesDirectoryLink} href={href}>
                      {label}
                    </Link>
                  </span>
                );
              })}
            </div>
            <div className={css.professionalDirectoryLink}>
              <Link href="/s?pub_listingType=professional-directory">
                Servicios Profesionales
              </Link>
            </div>
          </div>
        ) : (
          <div
            className={classNames(defaultClasses.blockContainer, getColumnCSS(numColumns), {
              [css.noSidePaddings]: isInsideContainer,
              [css.customerOptionsGrid]: isCustomerOptionsSection,
              [css.providerOptionsGrid]: isProviderOptionsSection,
            })}
          >
            {isProviderOptionsSection ? (
              blocks.map((block, index) => (
                <ProviderOptionCard
                  key={block.blockId || `${sectionId}-provider-option-${index + 1}`}
                  block={block}
                  href={PROVIDER_OPTION_DESTINATIONS[index]}
                  responsiveImageSizes={getResponsiveImageSizes(4)}
                  options={options}
                />
              ))
            ) : (
              <BlockBuilder
                ctaButtonClass={defaultClasses.ctaButton}
                className={isCustomerOptionsSection ? css.customerOptionCard : null}
                mediaClassName={isCustomerOptionsSection ? css.customerOptionMedia : null}
                textClassName={isCustomerOptionsSection ? css.customerOptionText : null}
                suppressLinkTitle={isCustomerOptionsSection}
                blocks={blocks}
                sectionId={sectionId}
                responsiveImageSizes={getResponsiveImageSizes(numColumns)}
                options={options}
              />
            )}
          </div>
        )
      ) : null}
    </SectionContainer>
  );
};

export default SectionColumns;
